import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const ManualPage: React.FC = () => {
  const { t } = useTranslation('manual');
  const location = useLocation();
  const [initialScrollTarget, setInitialScrollTarget] = useState<string | null>(null);

  const sections = [
    { key: 'gettingStarted', title: t('gettingStarted.title') },
    { key: 'dashboard', title: t('dashboard.title') },
    { key: 'accounts', title: t('accounts.title') },
    { key: 'journals', title: t('journals.title') },
    { key: 'invoices', title: t('invoices.title') },
    { key: 'taxReports', title: t('taxReports.title') },
    { key: 'bankAccounts', title: t('bankAccounts.title') },
    { key: 'bankReconciliation', title: t('bankReconciliation.title') },
    { key: 'fiscalYear', title: t('fiscalYear.title') },
    { key: 'periodClosing', title: t('periodClosing.title') },
    { key: 'reports', title: t('reports.title') },
    { key: 'dataManagement', title: t('dataManagement.title') }
  ];

  // 導覽系統對應的章節映射
  const navigationToSectionMap: Record<string, string> = {
    '/': 'dashboard',
    '/accounts': 'accounts',
    '/journals': 'journals',
    '/invoices': 'invoices',
    '/tax-reports': 'taxReports',
    '/bank-accounts': 'bankAccounts',
    '/bank-reconciliation': 'bankReconciliation',
    '/period-management': 'periodClosing',
    '/period-comparison': 'periodClosing',
    '/reports': 'reports',
    '/data-io': 'dataManagement'
  };

  // 在頁面載入時判斷初始滾動目標
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const fromSystem = urlParams.get('from');
    let targetSection = '';

    // 優先級1: URL 查詢參數
    if (fromSystem && navigationToSectionMap[fromSystem]) {
      targetSection = navigationToSectionMap[fromSystem];
    }
    else {
      // 優先級2: sessionStorage 中記錄的最後活動頁面
      const lastActivePage = sessionStorage.getItem('lastActivePage');

      // 特殊處理：如果 lastActivePage 是儀表板，跳到頂部
      if (lastActivePage === '/') {
        targetSection = '';
      }
      else if (lastActivePage && navigationToSectionMap[lastActivePage]) {
        targetSection = navigationToSectionMap[lastActivePage];
      }

      // 優先級3: 備用方案檢查 referrer
      if (!targetSection) {
        try {
          const referrer = document.referrer;

          if (referrer) {
            const referrerUrl = new URL(referrer);
            const referrerPath = referrerUrl.pathname;

            if (navigationToSectionMap[referrerPath]) {
              targetSection = navigationToSectionMap[referrerPath];
            }
          }
        } catch (error) {
          // URL 解析失敗時忽略
        }
      }

      // 優先級4: URL hash
      if (!targetSection && location.hash) {
        const hashTarget = location.hash.substring(1);
        // 檢查是否為有效章節
        if (sections.find(section => section.key === hashTarget)) {
          targetSection = hashTarget;
        }
      }
    }

    // 設定初始滾動目標
    setInitialScrollTarget(targetSection || null);
  }, [location.search, location.hash]);

  // 在頁面內容載入後執行初始滾動
  useEffect(() => {
    if (initialScrollTarget) {
      // 立即執行初始滾動，不等待狀態變更
      const executeScroll = () => {
        const element = document.getElementById(initialScrollTarget);
        if (element) {
          scrollToSection(initialScrollTarget);
        } else {
          // 如果元素還沒渲染，稍等片刻再試
          setTimeout(executeScroll, 10);
        }
      };

      // 使用 requestAnimationFrame 確保在下一個渲染循環執行
      requestAnimationFrame(executeScroll);
    }
  }, [initialScrollTarget]);

  const scrollToSection = (sectionKey: string) => {
    const element = document.getElementById(sectionKey);
    if (element) {
      const elementPosition = element.offsetTop;
      const offsetPosition = elementPosition - 30; // 偏移，確保標題不被遮住

      window.scrollTo({
        top: offsetPosition,
        behavior: 'auto'
      });
    }
  };

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-8">
        {t('title')}
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-6 gap-8">
          {/* 側邊目錄 */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              <nav className="bg-white dark:bg-slate-800 rounded-lg shadow p-6">
                <h2 className="font-semibold text-gray-900 dark:text-white mb-4">{t('tableOfContents.title')}</h2>
                <ul className="space-y-2">
                  {sections.map((section) => (
                    <li key={section.key}>
                      <button
                        onClick={() => scrollToSection(section.key)}
                        className="text-left w-full text-brand-600 hover:text-brand-800 dark:text-brand-400 dark:hover:text-brand-300 hover:underline"
                      >
                        {section.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </div>

          {/* 主要內容 */}
          <div className="lg:col-span-5">

              {/* 快速入門 */}
              <section id="gettingStarted" className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">{t('gettingStarted.title')}</h2>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3">{t('gettingStarted.welcome')}</h3>
                  <p className="text-blue-800 dark:text-blue-200">
                    {t('gettingStarted.description')}
                  </p>
                </div>
                <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">{t('gettingStarted.features')}：</h4>
                <ul className="list-disc pl-6 space-y-2 mb-6 text-gray-700 dark:text-gray-300">
                  <li>{t('gettingStarted.feature1')}</li>
                  <li>{t('gettingStarted.feature2')}</li>
                  <li>{t('gettingStarted.feature3')}</li>
                  <li>{t('gettingStarted.feature4')}</li>
                  <li>{t('gettingStarted.feature5')}</li>
                </ul>
              </section>

              {/* 儀表板 */}
              <section id="dashboard" className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">{t('dashboard.title')}</h2>
                <p className="mb-4 text-gray-700 dark:text-gray-300">
                  {t('dashboard.intro')}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">{t('dashboard.financialOverview')}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('dashboard.financialOverviewDesc')}
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">{t('dashboard.incomeExpenseStats')}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('dashboard.incomeExpenseStatsDesc')}
                    </p>
                  </div>
                </div>
              </section>

              {/* 會計科目管理 */}
              <section id="accounts" className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">{t('accounts.title')}</h2>
                <p className="mb-4 text-gray-700 dark:text-gray-300">
                  {t('accounts.intro')}
                </p>
                <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">{t('accounts.mainFeatures')}：</h4>
                <ul className="list-disc pl-6 space-y-2 mb-6 text-gray-700 dark:text-gray-300">
                  <li><strong>{t('accounts.defaultInit')}：</strong>{t('accounts.defaultInitDesc')}</li>
                  <li><strong>{t('accounts.categoryManagement')}：</strong>{t('accounts.categoryManagementDesc')}</li>
                  <li><strong>{t('accounts.customAccounts')}：</strong>{t('accounts.customAccountsDesc')}</li>
                  <li><strong>{t('accounts.balanceTracking')}：</strong>{t('accounts.balanceTrackingDesc')}</li>
                </ul>
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <h4 className="font-semibold text-green-900 dark:text-green-100 mb-2">💡 {t('accounts.usageTipsTitle')}</h4>
                  <p className="text-green-800 dark:text-green-200 text-sm">
                    {t('accounts.usageTips')}
                  </p>
                </div>
              </section>

              {/* 記帳分錄 */}
              <section id="journals" className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">{t('journals.title')}</h2>
                <p className="mb-4 text-gray-700 dark:text-gray-300">
                  {t('journals.intro')}
                </p>
                <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">{t('journals.coreFeatures')}：</h4>
                <ul className="list-disc pl-6 space-y-2 mb-6 text-gray-700 dark:text-gray-300">
                  <li><strong>{t('journals.doubleEntry')}：</strong>{t('journals.doubleEntryDesc')}</li>
                  <li><strong>{t('journals.autoBalance')}：</strong>{t('journals.autoBalanceDesc')}</li>
                  <li><strong>{t('journals.statusManagement')}：</strong>{t('journals.statusManagementDesc')}</li>
                  <li><strong>{t('journals.batchProcessing')}：</strong>{t('journals.batchProcessingDesc')}</li>
                </ul>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                  <h4 className="font-semibold text-yellow-900 dark:text-yellow-100 mb-2">⚠️ {t('journals.noticeTitle')}</h4>
                  <p className="text-yellow-800 dark:text-yellow-200 text-sm">
                    {t('journals.notice')}
                  </p>
                </div>
              </section>

              {/* 發票管理 */}
              <section id="invoices" className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">{t('invoices.title')}</h2>
                <p className="mb-4 text-gray-700 dark:text-gray-300">
                  {t('invoices.intro')}
                </p>
                <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">{t('invoices.highlights')}：</h4>
                <ul className="list-disc pl-6 space-y-2 mb-6 text-gray-700 dark:text-gray-300">
                  <li><strong>{t('invoices.registration')}：</strong>{t('invoices.registrationDesc')}</li>
                  <li><strong>{t('invoices.statusTracking')}：</strong>{t('invoices.statusTrackingDesc')}</li>
                  <li><strong>{t('invoices.taxCalculation')}：</strong>{t('invoices.taxCalculationDesc')}</li>
                  <li><strong>{t('invoices.reportOutput')}：</strong>{t('invoices.reportOutputDesc')}</li>
                </ul>
              </section>

              {/* 稅務計算 (保持原樣，內容較長且結構複雜) */}
              <section id="taxReports" className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">{t('taxReports.title')}</h2>
                <p className="mb-4 text-gray-700 dark:text-gray-300">
                  {t('taxReports.intro')}
                </p>
                <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">{t('taxReports.coreFeatures')}：</h4>
                <ul className="list-disc pl-6 space-y-2 mb-6 text-gray-700 dark:text-gray-300">
                  <li><strong>{t('taxReports.form401')}：</strong>{t('taxReports.form401Desc')}</li>
                  <li><strong>{t('taxReports.form403')}：</strong>{t('taxReports.form403Desc')}</li>
                  <li><strong>{t('taxReports.taxEstimate')}：</strong>{t('taxReports.taxEstimateDesc')}</li>
                  <li><strong>{t('taxReports.periodSelection')}：</strong>{t('taxReports.periodSelectionDesc')}</li>
                  <li><strong>{t('taxReports.detailTracking')}：</strong>{t('taxReports.detailTrackingDesc')}</li>
                </ul>

                <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-4 mb-6">
                  <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">{t('taxReports.calculationPrinciples')}：</h4>
                  <div className="space-y-3">
                    <div className="border-l-4 border-green-500 pl-4">
                      <p className="font-semibold text-green-700 dark:text-green-400">{t('taxReports.outputTax')}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{t('taxReports.outputTaxFormula')}</p>
                    </div>
                    <div className="border-l-4 border-blue-500 pl-4">
                      <p className="font-semibold text-blue-700 dark:text-blue-400">{t('taxReports.inputTax')}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{t('taxReports.inputTaxFormula')}</p>
                    </div>
                    <div className="border-l-4 border-red-500 pl-4">
                      <p className="font-semibold text-red-700 dark:text-red-400">{t('taxReports.netTaxPayable')}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{t('taxReports.netTaxPayableFormula')}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">📊 {t('taxReports.taxOverview')}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('taxReports.taxOverviewDesc')}
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">📋 {t('taxReports.returnDetails')}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('taxReports.returnDetailsDesc')}
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">🖨️ {t('taxReports.printFeature')}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('taxReports.printFeatureDesc')}
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">📄 {t('taxReports.pdfExport')}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('taxReports.pdfExportDesc')}
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-4 mb-6">
                  <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">{t('taxReports.usageFlow')}：</h4>
                  <ol className="list-decimal pl-6 space-y-3 text-gray-700 dark:text-gray-300">
                    <li><strong>{t('taxReports.step1')}：</strong>{t('taxReports.step1Desc')}</li>
                    <li><strong>{t('taxReports.step2')}：</strong>{t('taxReports.step2Desc')}</li>
                    <li><strong>{t('taxReports.step3')}：</strong>{t('taxReports.step3Desc')}</li>
                    <li><strong>{t('taxReports.step4')}：</strong>{t('taxReports.step4Desc')}</li>
                    <li><strong>{t('taxReports.step5')}：</strong>{t('taxReports.step5Desc')}</li>
                  </ol>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                  <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">💡 {t('taxReports.usageTipsTitle')}</h4>
                  <ul className="text-blue-800 dark:text-blue-200 text-sm space-y-1">
                    <li>• {t('taxReports.tip1')}</li>
                    <li>• {t('taxReports.tip2')}</li>
                    <li>• {t('taxReports.tip3')}</li>
                    <li>• {t('taxReports.tip4')}</li>
                  </ul>
                </div>

                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6">
                  <h4 className="font-semibold text-green-900 dark:text-green-100 mb-2">🎯 {t('taxReports.taxCalculationStandardsTitle')}</h4>
                  <p className="text-green-800 dark:text-green-200 text-sm mb-2">
                    {t('taxReports.taxCalculationStandards')}
                  </p>
                  <ul className="text-green-800 dark:text-green-200 text-sm space-y-1">
                    <li>• {t('taxReports.standard1')}</li>
                    <li>• {t('taxReports.standard2')}</li>
                    <li>• {t('taxReports.standard3')}</li>
                  </ul>
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                  <h4 className="font-semibold text-yellow-900 dark:text-yellow-100 mb-2">⚠️ {t('taxReports.noticeTitle')}</h4>
                  <ul className="text-yellow-800 dark:text-yellow-200 text-sm space-y-1">
                    <li>• {t('taxReports.notice1')}</li>
                    <li>• {t('taxReports.notice2')}</li>
                    <li>• {t('taxReports.notice3')}</li>
                    <li>• {t('taxReports.notice4')}</li>
                  </ul>
                </div>
              </section>

              {/* 銀行帳戶管理 */}
              <section id="bankAccounts" className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">{t('bankAccounts.title')}</h2>
                <p className="mb-4 text-gray-700 dark:text-gray-300">
                  {t('bankAccounts.intro')}
                </p>
                <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">{t('bankAccounts.coreFeatures')}：</h4>
                <ul className="list-disc pl-6 space-y-2 mb-6 text-gray-700 dark:text-gray-300">
                  <li><strong>{t('bankAccounts.accountCreation')}：</strong>{t('bankAccounts.accountCreationDesc')}</li>
                  <li><strong>{t('bankAccounts.balanceManagement')}：</strong>{t('bankAccounts.balanceManagementDesc')}</li>
                  <li><strong>{t('bankAccounts.accountStatus')}：</strong>{t('bankAccounts.accountStatusDesc')}</li>
                  <li><strong>{t('bankAccounts.statsSummary')}：</strong>{t('bankAccounts.statsSummaryDesc')}</li>
                </ul>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">{t('bankAccounts.accountTypes')}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('bankAccounts.accountTypesDesc')}
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">{t('bankAccounts.realtimeBalance')}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('bankAccounts.realtimeBalanceDesc')}
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">{t('bankAccounts.statsReports')}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('bankAccounts.statsReportsDesc')}
                    </p>
                  </div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">💡 {t('bankAccounts.usageTipsTitle')}</h4>
                  <p className="text-blue-800 dark:text-blue-200 text-sm">
                    {t('bankAccounts.usageTips')}
                  </p>
                </div>
              </section>

              {/* 銀行對帳 */}
              <section id="bankReconciliation" className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">{t('bankReconciliation.title')}</h2>
                <p className="mb-4 text-gray-700 dark:text-gray-300">
                  {t('bankReconciliation.intro')}
                </p>
                <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">{t('bankReconciliation.features')}：</h4>
                <ul className="list-disc pl-6 space-y-2 mb-6 text-gray-700 dark:text-gray-300">
                  <li><strong>{t('bankReconciliation.statementImport')}：</strong>{t('bankReconciliation.statementImportDesc')}</li>
                  <li><strong>{t('bankReconciliation.smartMatching')}：</strong>{t('bankReconciliation.smartMatchingDesc')}</li>
                  <li><strong>{t('bankReconciliation.fieldMapping')}：</strong>{t('bankReconciliation.fieldMappingDesc')}</li>
                  <li><strong>{t('bankReconciliation.importHistory')}：</strong>{t('bankReconciliation.importHistoryDesc')}</li>
                </ul>
                <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-4 mb-6">
                  <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">{t('bankReconciliation.reconciliationFlow')}：</h4>
                  <ol className="list-decimal pl-6 space-y-3 text-gray-700 dark:text-gray-300">
                    <li><strong>{t('bankReconciliation.flowStep1')}：</strong>{t('bankReconciliation.flowStep1Desc')}</li>
                    <li><strong>{t('bankReconciliation.flowStep2')}：</strong>{t('bankReconciliation.flowStep2Desc')}</li>
                    <li><strong>{t('bankReconciliation.flowStep3')}：</strong>{t('bankReconciliation.flowStep3Desc')}</li>
                    <li><strong>{t('bankReconciliation.flowStep4')}：</strong>{t('bankReconciliation.flowStep4Desc')}</li>
                    <li><strong>{t('bankReconciliation.flowStep5')}：</strong>{t('bankReconciliation.flowStep5Desc')}</li>
                  </ol>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6">
                  <h4 className="font-semibold text-green-900 dark:text-green-100 mb-2">🏦 {t('bankReconciliation.supportedBanksTitle')}</h4>
                  <p className="text-green-800 dark:text-green-200 text-sm mb-2">
                    {t('bankReconciliation.supportedBanks')}
                  </p>
                  <ul className="text-green-800 dark:text-green-200 text-sm list-disc pl-4">
                    <li>{t('bankReconciliation.bank1')}</li>
                    <li>{t('bankReconciliation.bank2')}</li>
                    <li>{t('bankReconciliation.bank3')}</li>
                  </ul>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                  <h4 className="font-semibold text-yellow-900 dark:text-yellow-100 mb-2">⚠️ {t('bankReconciliation.noticeTitle')}</h4>
                  <ul className="text-yellow-800 dark:text-yellow-200 text-sm space-y-1">
                    <li>• {t('bankReconciliation.notice1')}</li>
                    <li>• {t('bankReconciliation.notice2')}</li>
                    <li>• {t('bankReconciliation.notice3')}</li>
                    <li>• {t('bankReconciliation.notice4')}</li>
                  </ul>
                </div>
              </section>

              {/* 會計年度 */}
              <section id="fiscalYear" className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">{t('fiscalYear.title')}</h2>
                <p className="mb-4 text-gray-700 dark:text-gray-300">
                  {t('fiscalYear.intro')}
                </p>
                <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">{t('fiscalYear.managementFeatures')}：</h4>
                <ul className="list-disc pl-6 space-y-2 mb-6 text-gray-700 dark:text-gray-300">
                  <li><strong>{t('fiscalYear.yearSetup')}：</strong>{t('fiscalYear.yearSetupDesc')}</li>
                  <li><strong>{t('fiscalYear.periodDivision')}：</strong>{t('fiscalYear.periodDivisionDesc')}</li>
                  <li><strong>{t('fiscalYear.yearSwitch')}：</strong>{t('fiscalYear.yearSwitchDesc')}</li>
                  <li><strong>{t('fiscalYear.multiYearComparison')}：</strong>{t('fiscalYear.multiYearComparisonDesc')}</li>
                </ul>
              </section>

              {/* 期間結算 */}
              <section id="periodClosing" className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">{t('periodClosing.title')}</h2>
                <p className="mb-4 text-gray-700 dark:text-gray-300">
                  {t('periodClosing.intro')}
                </p>
                <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">{t('periodClosing.closingProcess')}：</h4>
                <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-4 mb-6">
                  <ol className="list-decimal pl-6 space-y-3 text-gray-700 dark:text-gray-300">
                    <li><strong>{t('periodClosing.processStep1')}：</strong>{t('periodClosing.processStep1Desc')}</li>
                    <li><strong>{t('periodClosing.processStep2')}：</strong>{t('periodClosing.processStep2Desc')}</li>
                    <li><strong>{t('periodClosing.processStep3')}：</strong>{t('periodClosing.processStep3Desc')}</li>
                    <li><strong>{t('periodClosing.processStep4')}：</strong>{t('periodClosing.processStep4Desc')}</li>
                  </ol>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">📊 {t('periodClosing.benefitsTitle')}</h4>
                  <p className="text-blue-800 dark:text-blue-200 text-sm">
                    {t('periodClosing.benefits')}
                  </p>
                </div>
              </section>

              {/* 財務報表 */}
              <section id="reports" className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">{t('reports.title')}</h2>
                <p className="mb-4 text-gray-700 dark:text-gray-300">
                  {t('reports.intro')}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">{t('reports.balanceSheet')}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('reports.balanceSheetDesc')}
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">{t('reports.incomeStatement')}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('reports.incomeStatementDesc')}
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2 text-gray-900 dark:text-white">{t('reports.trialBalance')}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('reports.trialBalanceDesc')}
                    </p>
                  </div>
                </div>
                <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">{t('reports.reportFeatures')}：</h4>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                  <li>{t('reports.feature1')}</li>
                  <li>{t('reports.feature2')}</li>
                  <li>{t('reports.feature3')}</li>
                  <li>{t('reports.feature4')}</li>
                </ul>
              </section>

              {/* 資料管理 */}
              <section id="dataManagement" className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">{t('dataManagement.title')}</h2>
                <p className="mb-4 text-gray-700 dark:text-gray-300">
                  {t('dataManagement.intro')}
                </p>
                <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">{t('dataManagement.managementFeatures')}：</h4>
                <ul className="list-disc pl-6 space-y-2 mb-6 text-gray-700 dark:text-gray-300">
                  <li><strong>{t('dataManagement.batchImport')}：</strong>{t('dataManagement.batchImportDesc')}</li>
                  <li><strong>{t('dataManagement.dataExport')}：</strong>{t('dataManagement.dataExportDesc')}</li>
                  <li><strong>{t('dataManagement.dataBackup')}：</strong>{t('dataManagement.dataBackupDesc')}</li>
                  <li><strong>{t('dataManagement.systemIntegration')}：</strong>{t('dataManagement.systemIntegrationDesc')}</li>
                </ul>
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <h4 className="font-semibold text-red-900 dark:text-red-100 mb-2">🔒 {t('dataManagement.securityReminderTitle')}</h4>
                  <p className="text-red-800 dark:text-red-200 text-sm">
                    {t('dataManagement.securityReminder')}
                  </p>
                </div>
              </section>

          </div>
        </div>
    </div>
  );
};

export default ManualPage;
